import { set } from 'vue'
import moment from 'moment'

const BATCH_SIZE = 25

// map quarters in format 1-2024, 2-2024, 3-2024, 4-2024
const lastYearFromNowQuarters = Array.from({ length: 4 }, (_, i) => {
    const quarter = moment().subtract(i + 1, 'quarter')
    const value = `${quarter.quarter()}-${quarter.year()}`
    return {
        label: value,
        value
    }
})

const emptyCollection = () => ({ list: [], resultSet: {} })

const state = () => ({
    reclamations: emptyCollection(),
    contractors: emptyCollection(),
    status: 'idle',
    alias: '',
    filters: {
        status: {
            type: 'radio',
            value: '',
            text: 'Status reklamacji',
            options: [
                {
                    label: 'Wszystkie',
                    value: ''
                },
                {
                    label: 'W toku',
                    value: 'P'
                },
                {
                    label: 'Zaakceptowane',
                    value: 'Z'
                },
                {
                    label: 'Odrzucone',
                    value: 'X'
                }
            ]
        },
        locationId: {
            additionalText: 'Sklep',
            type: 'ui-virtual-list',
            value: null,
            multi: false,
            searcher: false,
            label: 'Sklep',
            moduleName: 'merchants',
            fetchAction: 'merchantsFetch',
            getter: 'retrReclamations/locationsByNetworkAlias',
            disabledLinkGenerator: true
        },
        quarter: {
            additionalText: 'Kwartał',
            type: 'ui-virtual-list',
            value: null,
            multi: false,
            searcher: false,
            label: 'Kwartał',
            options: lastYearFromNowQuarters
        },
        contractor: {
            additionalText: 'Kontrahent',
            type: 'ui-virtual-list',
            value: null,
            multi: false,
            searcher: true,
            label: 'Kontrahent',
            fetchAction: 'retrReclamations/loadContractors',
            getter: 'retrReclamations/contractors',
            fetchOnInit: false
        }
    },
    sortOptions: [
        {
            query: 'RECLAMATION_DATE',
            label: 'Data reklamacji'
        },
        {
            query: 'CONTRACTOR',
            label: 'Kontrahent'
        }
    ],
    currentSort: {
        by: 'RECLAMATION_DATE',
        direction: 'desc'
    },
    limit: BATCH_SIZE // results per page
})

const mutations = {
    SET_FILTER(state, { filter, value }) {
        set(state.filters[filter], 'value', value)
    },
    RESET_FILTERS(state, payload) {
        set(state, 'filters', payload)
    },
    SET_STATUS(state, payload) {
        state.status = payload
    },
    SET_DATA(state, payload) {
        state.reclamations = payload
    },
    SET_ALIAS(state, payload) {
        state.alias = payload
    },
    SET_CONTRACTORS(state, payload) {
        state.contractors = payload
    },
    SET_LIMIT(state, payload) {
        state.limit = payload
    }
}

const actions = {
    async fetchReclamations({ rootGetters }, payload) {
        try {
            const userId = rootGetters.userId
            const endpoint = `/users/${userId}/reclamation/list`
            const { config, ...params } = payload
            return await this.$api.post(endpoint, params, config)
        } catch (error) {
            console.error(error)
        }
    },

    async loadReclamations({ commit, dispatch, state }, payload = {}) {
        try {
            commit('SET_STATUS', 'loading')

            const locationId = state.filters.locationId.value || undefined

            if (!locationId) {
                // Reset data, when no location selected
                commit('SET_DATA', emptyCollection())
                return
            }

            // Set payload
            const quarterSplit = (state.filters.quarter.value || '').split('-')
            const contractor = state.filters.contractor.value || undefined
            const offset = (payload.page - 1 || 0) * BATCH_SIZE

            const { status, data } = await dispatch('fetchReclamations', {
                locationId,
                quarter: +quarterSplit[0] || undefined,
                year: +quarterSplit[1] || undefined,
                contractor,
                status: state.filters.status.value || undefined,
                offset,
                limit: state.limit || BATCH_SIZE,
                direction: state.currentSort.direction,
                sorting: state.currentSort.by
            })

            if (status !== 200 || !data?.data) {
                throw new Error(data?.message || 'Error GET loadReclamations')
            }

            const { list, resultSet } = data.data
            commit('SET_DATA', {
                list,
                resultSet
            })
        } catch (error) {
            commit('SET_DATA', null)
            console.error(error)
        } finally {
            commit('SET_STATUS', 'idle')
        }
    },

    async loadContractors({ state, commit, rootGetters, getters }, payload) {
        const { offset, limit, page, query } = payload || {}

        const userId = rootGetters.userId
        const locationId =
            state.filters.locationId.value ||
            (getters.locationsByNetworkAlias || [])[0]?.value

        if (!locationId) {
            // Reset data, when no location selected
            return commit('SET_CONTRACTORS', emptyCollection())
        }

        const params = {
            locationId,
            offset: offset || state.contractors.list.length || 0,
            limit: limit || 10,
            contractorName: query
        }

        if (page && page === 1) {
            params.offset = 0
            commit('SET_CONTRACTORS', emptyCollection())
        }

        try {
            const { status, data } = await this.$api.get(`/users/${userId}/retr/contractors`, {
                params
            })

            if (status !== 200 || !data?.data) {
                throw new Error(data?.message || `Error GET /users/${userId}/retr/contractors`)
            }

            const { list, resultSet } = data.data

            commit('SET_CONTRACTORS', {
                list: [...(state.contractors.list || []), ...list],
                resultSet
            })
        } catch(err) {
            console.error(err)
        }
    },

    resetReclamations({ commit }) {
        commit('SET_DATA', emptyCollection())
    },

    resetContractors({ commit }) {
        commit('SET_CONTRACTORS', emptyCollection())
    },

    setDefaultLocationId({ commit, getters }) {
        const locationId = getters.locationsByNetworkAlias[0].value
        commit('SET_FILTER', { filter: 'locationId', value: locationId })
    },

    setDefaultQuarter({ commit }) {
        const quarter = lastYearFromNowQuarters[0].value
        commit('SET_FILTER', { filter: 'quarter', value: quarter })
    }
}

const getters = {
    reclamations: state => state.reclamations,
    filters: state => state.filters,
    status: state => state.status,
    contractors: state => {
        return (state.contractors.list || []).map(contractor => ({
            label: contractor,
            value: contractor
        }))
    },
    locationsByNetworkAlias: (state, __, ___, rootGetters) => {
        return rootGetters.getLocationsByNetworkAlias(state.alias)
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}